import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@cartcloser/ui';
import { createStore, fetchMe, seedDemo } from '../lib/api.js';
import { useQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

const ERROR_COPY: Record<string, string> = {
  invalid_name: 'Please enter a store name.',
  invalid_slug: 'Slugs use lowercase letters, numbers and single hyphens (3–32 chars).',
  reserved_slug: 'That slug is reserved. Pick another.',
  slug_taken: 'That slug is already taken.',
  unauthorized: 'Please sign in again.',
};

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}

function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const effectiveSlug = slugTouched ? slug : slugify(name);

  async function loadDemo() {
    setError(null);
    setSeeding(true);
    try {
      const r = await seedDemo();
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await navigate({ to: '/admin/$slug', params: { slug: r.slug } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not seed the demo store.');
      setSeeding(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await createStore({ name, slug: effectiveSlug });
    setLoading(false);
    if (!result.ok || !result.slug) {
      setError(ERROR_COPY[result.error ?? ''] ?? 'Could not create the store.');
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    await navigate({ to: '/admin/$slug', params: { slug: result.slug } });
  }

  return (
    <main className="mx-auto flex max-w-md flex-col gap-6 px-6 py-20">
      {me.data?.user.isSuperAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle>Load demo store</CardTitle>
            <CardDescription>
              Seed “Acme Appliances” — 4 expert agents (Dishwashers, Fridges, Washing Machines,
              Dryers) and a realistic catalogue. Great for QA.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" loading={seeding} onClick={loadDemo}>
              Seed Acme Appliances
            </Button>
          </CardContent>
        </Card>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>Create your store</CardTitle>
          <CardDescription>
            This is your CartCloser workspace — your catalogue, agents, and analytics live here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <FormField label="Store name" required>
              {({ id }) => (
                <Input
                  id={id}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Volt Appliances"
                  required
                />
              )}
            </FormField>
            <FormField
              label="Slug"
              description={`Your store lives at ${window.location.host}/${effectiveSlug || 'your-slug'}`}
              required
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={effectiveSlug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(slugify(e.target.value));
                  }}
                  required
                />
              )}
            </FormField>
            <Button type="submit" loading={loading} className="w-full">
              Create store
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
